import React from "react"
import PropTypes from "prop-types"
import { Dropdown, Flex } from "antd"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { routes } from "router/routes"
import { Text } from "ui/Typography"
import { Icon } from "ui/Icon"
import BlackoutScopeTag from "ui/Blackout/BlackoutScopeTag"
import { BLACKOUT_SCOPES } from "../constants"
import { formatRowDateLabel } from "../utils"
import styles from "./BlackoutRow.module.css"

/** Scopes whose summary counts unavailable resources alongside the affected listings. */
const RESOURCE_SCOPES = [BLACKOUT_SCOPES.CREW, BLACKOUT_SCOPES.EQUIPMENT]

const BlackoutRow = ({ blackout, menuItems, onReviewConflicts }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { scope, reason, recurring_yearly: isRecurringYearly, impact } = blackout
  const listingsCount = impact?.listings_count || 0
  const resources = impact?.resources || []
  const conflictingCount = impact?.conflicting_bookings_count || 0
  const isResourceScope = RESOURCE_SCOPES.includes(scope)

  /** "View" leaves the settings row for the listings page rather than expanding a list in place. */
  const goToListings = () => navigate(routes.proExperiencesPath())

  const affectedLabel = isResourceScope
    ? t("settings.blackout.resources_affected", {
        defaultValue: "{{resources}} unavailable → {{count}} listings affected",
        resources: resources.length
          ? t("settings.blackout.resource_count", { defaultValue: "{{count}} resources", count: resources.length })
          : "",
        count: listingsCount
      })
    : t("settings.blackout.listings_affected", {
        defaultValue: "{{count}} listings affected",
        count: listingsCount
      })

  return (
    <article className={styles.row}>
      <Flex align="flex-start" justify="space-between" gap="var(--spacing-s)">
        <div className={styles.main}>
          <Flex align="center" gap="var(--spacing-xxs)" className={styles.dateLine}>
            <Text variant="sub" size="md" fw="500">
              {formatRowDateLabel(blackout)}
            </Text>
            {isRecurringYearly && (
              <span className={styles.recurringIcon} aria-label={t("settings.blackout.repeats_yearly", { defaultValue: "Repeats each year" })}>
                <Icon name="Repeat" width={14} height={14} />
              </span>
            )}
          </Flex>

          {reason && (
            <Flex align="center" gap="var(--spacing-xxs)" className={styles.reasonLine}>
              <Icon name="Clipboard" width={14} height={14} />
              <Text variant="caption" size="lg" className={styles.reasonText}>
                {reason}
              </Text>
            </Flex>
          )}

          <Flex align="center" gap="var(--spacing-xs)" wrap className={styles.affectsLine}>
            <Text variant="caption" size="lg" className={styles.affectsText}>
              {affectedLabel}
            </Text>
            {listingsCount > 0 && (
              <button type="button" className={styles.linkButton} onClick={goToListings}>
                {t("settings.blackout.view", { defaultValue: "View" })}
                <Icon name="ExternalLink" width={12} height={12} />
              </button>
            )}
          </Flex>
        </div>

        <Flex align="center" gap="var(--spacing-xs)" className={styles.actions}>
          <BlackoutScopeTag scope={scope} />
          <Dropdown menu={{ items: menuItems }} trigger={["click"]} placement="bottomRight">
            <button type="button" className={styles.menuButton} aria-label={t("settings.blackout.row_actions", { defaultValue: "Blackout actions" })}>
              <Icon name="MoreHorizontal" width={18} height={18} />
            </button>
          </Dropdown>
        </Flex>
      </Flex>

      {conflictingCount > 0 && (
        <Flex align="center" gap="var(--spacing-xs)" className={styles.conflictBanner}>
          <Icon name="AlertTriangle" width={14} height={14} />
          <Text variant="caption" size="lg" className={styles.conflictText}>
            {t("settings.blackout.conflicts_warning", {
              defaultValue: "{{count}} bookings require cancellation or rescheduling",
              count: conflictingCount
            })}
          </Text>
          <button type="button" className={styles.reviewLink} onClick={() => onReviewConflicts(blackout)}>
            {t("settings.blackout.review", { defaultValue: "Review" })}
            <Icon name="ExternalLink" width={12} height={12} />
          </button>
        </Flex>
      )}
    </article>
  )
}

BlackoutRow.propTypes = {
  blackout: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    scope: PropTypes.string,
    reason: PropTypes.string,
    recurring_yearly: PropTypes.bool,
    entries: PropTypes.array,
    impact: PropTypes.object
  }).isRequired,
  menuItems: PropTypes.array.isRequired,
  onReviewConflicts: PropTypes.func.isRequired
}

export default BlackoutRow
